// PWA Install Modal with desktop and iOS Safari instructions (WP-04).
import { useId, useRef } from 'react';
import { useI18n } from '../../lib/i18n';
import { usePwaInstall } from '../../lib/pwa';
import { useDialogA11y } from '../../lib/useDialogA11y';
import { CloseIcon, SmartphoneIcon, CheckCircleIcon } from '../icons';

interface InstallAppModalProps {
  onClose: () => void;
}

export default function InstallAppModal({ onClose }: InstallAppModalProps) {
  const { t } = useI18n();
  const { hasNativePrompt, isInstalled, isIos, promptInstall } = usePwaInstall();
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  useDialogA11y(true, panelRef, onClose);

  const handleInstallClick = async () => {
    const outcome = await promptInstall();
    if (outcome === 'accepted') {
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4 backdrop-blur-sm">
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="w-full max-w-md overflow-hidden rounded-2xl border border-line bg-white shadow-2xl dark:border-slate-800 dark:bg-slate-900"
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-line px-5 py-4 dark:border-slate-800">
          <div className="flex items-center gap-2.5">
            <img src="/favicon-192.png" alt="WaTag" className="h-9 w-9 rounded-xl shadow-sm" />
            <div>
              <h3 id={titleId} className="text-sm font-bold text-ink dark:text-slate-100">
                {t('pwa.installTitle') || 'Install WaTag App'}
              </h3>
              <p className="text-xs text-ink-3 dark:text-slate-400">
                {t('pwa.installSubtitle') || 'Omnichannel WhatsApp Marketing Suite'}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('common.close') || 'Close'}
            className="grid h-8 w-8 place-items-center rounded-lg text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"
          >
            <CloseIcon size={16} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {isInstalled ? (
            <div className="text-center py-4 space-y-2">
              <div className="mx-auto grid h-12 w-12 place-items-center rounded-full bg-emerald-100 text-emerald-600 dark:bg-emerald-950/40 dark:text-emerald-400">
                <CheckCircleIcon size={24} />
              </div>
              <h4 className="text-sm font-bold text-ink dark:text-slate-100">
                {t('pwa.alreadyInstalled') || 'WaTag is already installed!'}
              </h4>
              <p className="text-xs text-ink-3 dark:text-slate-400">
                {t('pwa.alreadyInstalledDesc') || 'You are running the official standalone progressive web app.'}
              </p>
            </div>
          ) : isIos ? (
            /* iOS Safari Instructions */
            <div className="space-y-3">
              <p className="text-xs text-ink-2 dark:text-slate-300">
                {t('pwa.iosInstructions') || 'To install WaTag on your iPhone or iPad:'}
              </p>
              <div className="rounded-xl border border-line bg-slate-50 p-4 space-y-3 text-xs text-ink dark:border-slate-800 dark:bg-slate-850 dark:text-slate-200">
                <div className="flex items-start gap-2.5">
                  <span className="grid h-5 w-5 shrink-0 place-items-center rounded-full bg-brand text-[11px] font-bold text-white">1</span>
                  <span>{t('pwa.iosStep1') || 'Tap the Share button in Safari toolbar (the box with an upward arrow ⎋).'}</span>
                </div>
                <div className="flex items-start gap-2.5">
                  <span className="grid h-5 w-5 shrink-0 place-items-center rounded-full bg-brand text-[11px] font-bold text-white">2</span>
                  <span>{t('pwa.iosStep2') || 'Scroll down and tap Add to Home Screen.'}</span>
                </div>
                <div className="flex items-start gap-2.5">
                  <span className="grid h-5 w-5 shrink-0 place-items-center rounded-full bg-brand text-[11px] font-bold text-white">3</span>
                  <span>{t('pwa.iosStep3') || 'Tap Add in the top right corner.'}</span>
                </div>
              </div>
            </div>
          ) : (
            /* Standard Desktop / Android PWA */
            <div className="space-y-3">
              <p className="text-xs leading-relaxed text-ink-2 dark:text-slate-300">
                {t('pwa.benefits') ||
                  'Install our app for instant message notifications, sound alerts, offline caching, and a dedicated desktop workspace window.'}
              </p>

              <div className="rounded-xl border border-line bg-slate-50 p-3.5 space-y-2 dark:border-slate-800 dark:bg-slate-850">
                <div className="flex items-center gap-2 text-xs font-semibold text-ink dark:text-slate-200">
                  <span className="text-emerald-500">✓</span>
                  <span>{t('pwa.benefitWindow') || 'Fast, distraction-free standalone window'}</span>
                </div>
                <div className="flex items-center gap-2 text-xs font-semibold text-ink dark:text-slate-200">
                  <span className="text-emerald-500">✓</span>
                  <span>{t('pwa.benefitNotifications') || 'Instant push notifications even when tab is closed'}</span>
                </div>
                <div className="flex items-center gap-2 text-xs font-semibold text-ink dark:text-slate-200">
                  <span className="text-emerald-500">✓</span>
                  <span>{t('pwa.benefitLaunch') || 'One-click launch from taskbar or desktop'}</span>
                </div>
              </div>

              {hasNativePrompt ? (
                <button
                  type="button"
                  onClick={handleInstallClick}
                  className="flex w-full items-center justify-center gap-2 rounded-xl bg-brand py-3 text-xs font-bold text-white shadow-md transition hover:bg-brand-dark"
                >
                  <SmartphoneIcon size={16} />
                  <span>{t('pwa.installButton') || 'Install App Now'}</span>
                </button>
              ) : (
                <div className="rounded-lg bg-amber-50 p-3 text-xs text-amber-800 dark:bg-amber-950/40 dark:text-amber-300">
                  {t('pwa.manualInstallHint') ||
                    'Click the Install icon in your browser address bar (top right in Chrome/Edge) to install WaTag.'}
                </div>
              )}
            </div>
          )}

          <div className="flex justify-end pt-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl border border-line px-4 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50 dark:border-slate-800 dark:text-slate-300 dark:hover:bg-slate-800"
            >
              {t('common.close') || 'Close'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
